import React, { useEffect, useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withI18next } from 'lib/i18n/withI18next'
import { segmentCookieNoticeResponded } from 'lib/segment'
import { calculateBottomGapOfAdsBanner } from 'lib/utils/common/commonUtils'
import StateStorage from 'lib/state-storage'
import { syncGoogleAdsConsent } from 'lib/tracking/google-ads-container'
import AlertAdsBanner from 'components/alert/alert-ads'
import CookieSetting from 'components/alert/alert-cookie/cookie-setting'
import styles from './style.scss'
import CookieBanner from './cookie-banner'

// Login used to write a literal "null" into this cookie for customers who had
// not answered the banner. That is not a choice, so it must not hide the banner.
const hasConsentChoice = (cookie) => {
  try {
    const consent = typeof cookie === 'string' ? JSON.parse(cookie) : cookie

    return Boolean(consent) && typeof consent === 'object'
  } catch (err) {
    return false
  }
}

const AlertCookie = ({ showLogin, showAdsBanner, isPhone }) => {
  const [isCookieBannerNotActive, setIsCookieBannerNotActive] = useState(true)
  const [isShowSetting, setIsShowSetting] = useState(false)
  const bannerPDPACookieConsent = StateStorage.getCookie(
    'bannerPDPACookieConsent',
  )
  const alertAdsBannerRef = useRef(null)
  const displayAdsBanner = showAdsBanner && !showLogin && !isPhone
  const showCookieBanner =
    !isCookieBannerNotActive &&
    !hasConsentChoice(bannerPDPACookieConsent) &&
    !isShowSetting &&
    !showLogin

  const showCookieBannerRef = useRef(showCookieBanner)

  useEffect(() => {
    showCookieBannerRef.current = showCookieBanner
  }, [showCookieBanner])

  // Logged-in customers are asked too: consent is not restored from the
  // account at login, so without an answer none of their orders are tracked.
  useEffect(() => {
    setIsCookieBannerNotActive(false)
  }, [])

  const updateAlertAdsBanner = React.useCallback(() => {
    if (!alertAdsBannerRef?.current) {
      return
    }

    const root = document.documentElement
    const percentageHorizontalScroll =
      (root.scrollTop / (root.scrollHeight - root.clientHeight)) * 100

    alertAdsBannerRef.current.style.marginBottom = `${calculateBottomGapOfAdsBanner(
      percentageHorizontalScroll,
    )}px`
  }, [])

  useEffect(() => {
    if (!showCookieBanner) {
      window.addEventListener('scroll', updateAlertAdsBanner)
    }

    return () => window.removeEventListener('scroll', updateAlertAdsBanner)
  }, [showCookieBanner, updateAlertAdsBanner])

  const disableCookieBanner = useCallback((type) => {
    const options = {
      is_performance_allowed: true,
      is_marketing_allowed: true,
    }

    setIsCookieBannerNotActive(true)
    switch (type) {
      case 'customize':
        setIsShowSetting(true)
        break
      case 'accept all':
        segmentCookieNoticeResponded(options)
        StateStorage.saveBannerStatus('PDPACookieConsent', options)
        syncGoogleAdsConsent()
        window.location.reload()
        break
      default:
    }
  }, [])

  return (
    <div className="alert-cookie">
      <style jsx>{styles}</style>
      {displayAdsBanner && (
        <div className="alert-cookie__ads-banner" ref={alertAdsBannerRef}>
          <AlertAdsBanner />
        </div>
      )}
      {showCookieBanner && (
        <CookieBanner
          disableBanner={(type) => {
            disableCookieBanner(type)
            updateAlertAdsBanner()
          }}
        />
      )}
      {isShowSetting && <CookieSetting />}
    </div>
  )
}

AlertCookie.propTypes = {
  showLogin: PropTypes.bool.isRequired,
  showAdsBanner: PropTypes.bool.isRequired,
  isPhone: PropTypes.bool.isRequired,
}

const Extended = withI18next()(AlertCookie)
export default connect((state) => ({
  showLogin: state.auth.showLogin,
}))(Extended)
